import React, { useState, useCallback, useEffect, useMemo } from "react";
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import Dashboard from "./Dashboard";
import Products from "./Products";
import Orders from "./Orders";
import Users from "./Users";
import Coupons from "./Coupons";
import Contacts from "./Contacts";
import Categories from "./Categories";
import Staff from "./Staff";
import "../../styles/admin.css";

import {
  FiTag,
  FiPackage,
  FiUsers,
  FiGift,
  FiMail,
  FiLogOut,
  FiChevronLeft,
  FiChevronRight,
  FiBarChart2,
  FiAlertCircle,
  FiCheck,
  FiClock,
  FiX,
  FiMenu,
  FiUserPlus,
  FiSearch,
  FiFilter,
  FiMoreHorizontal,
  FiEdit2,
  FiTrash2,
  FiEye,
  FiDownload,
  FiPlus,
  FiSettings,
  FiBell,
} from "react-icons/fi";
import {
  FaStore,
  FaCrown,
  FaTruck,
  FaCheckCircle,
  FaUserShield,
  FaUserTie,
  FaUser,
  FaRegGem,
  FaChartLine,
  FaShoppingBag,
  FaRegUser,
} from "react-icons/fa";
import {
  MdDashboard,
  MdCategory,
  MdOutlineDarkMode,
  MdOutlineLightMode,
} from "react-icons/md";

// ── Role config ────────────────────────────────────────────
const ROLE_HIERARCHY = { user: 0, employee: 1, admin: 2, superadmin: 3 };

const ROLE_META = {
  superadmin: {
    label: "Super Admin",
    icon: <FaUserShield />,
    color: "#f59e0b",
    gradient: "linear-gradient(135deg, #f59e0b, #f97316)",
  },
  admin: {
    label: "Administrator",
    icon: <FaCrown />,
    color: "#6366f1",
    gradient: "linear-gradient(135deg, #6366f1, #8b5cf6)",
  },
  employee: {
    label: "Employee",
    icon: <FaUserTie />,
    color: "#10b981",
    gradient: "linear-gradient(135deg, #10b981, #34d399)",
  },
  user: {
    label: "User",
    icon: <FaUser />,
    color: "#64748b",
    gradient: "linear-gradient(135deg, #64748b, #94a3b8)",
  },
};

export const C = {
  bg: "#f5f6fa",
  surface: "#ffffff",
  card: "#ffffff",
  border: "rgba(99, 102, 241, 0.1)",
  primary: "#6366f1",
  primaryDark: "#4f46e5",
  accent: "#06b6d4",
  gold: "#f59e0b",
  green: "#10b981",
  red: "#ef4444",
  orange: "#f97316",
  blue: "#3b82f6",
  text: "#0f172a",
  muted: "#64748b",
  white: "#ffffff",
};

export const STATUS_COLORS = {
  Processing: {
    bg: "rgba(245,158,11,0.1)",
    text: "#d97706",
    border: "rgba(245,158,11,0.25)",
  },
  Confirmed: {
    bg: "rgba(99,102,241,0.1)",
    text: "#6366f1",
    border: "rgba(99,102,241,0.25)",
  },
  Shipped: {
    bg: "rgba(6,182,212,0.1)",
    text: "#0891b2",
    border: "rgba(6,182,212,0.25)",
  },
  "Out for Delivery": {
    bg: "rgba(249,115,22,0.1)",
    text: "#ea580c",
    border: "rgba(249,115,22,0.25)",
  },
  Delivered: {
    bg: "rgba(16,185,129,0.1)",
    text: "#059669",
    border: "rgba(16,185,129,0.25)",
  },
  Cancelled: {
    bg: "rgba(239,68,68,0.1)",
    text: "#dc2626",
    border: "rgba(239,68,68,0.25)",
  },
};

// ── Enhanced Toast ─────────────────────────────────────────
export function Toast({ toasts, removeToast }) {
  return (
    <div className="toast-container-enhanced">
      {toasts.map((t) => (
        <div
          key={t.id}
          className={`toast-enhanced ${t.type === "error" ? "error" : "success"}`}
        >
          <div className="toast-enhanced-icon">
            {t.type === "error" ? <FiAlertCircle /> : <FiCheck />}
          </div>
          <div className="toast-enhanced-content">
            <p className="toast-enhanced-message">{t.msg}</p>
          </div>
          <button
            onClick={() => removeToast(t.id)}
            className="toast-enhanced-close"
          >
            <FiX />
          </button>
          <div className="toast-enhanced-progress" />
        </div>
      ))}
    </div>
  );
}

// ── Enhanced Stat Card ─────────────────────────────────────
export function Stat({
  icon,
  label,
  value,
  sub,
  trend,
  trendValue,
  color = C.primary,
}) {
  return (
    <div className="stat-card-enhanced">
      <div className="stat-card-enhanced-header">
        <div
          className="stat-card-enhanced-icon"
          style={{ background: `${color}15`, color }}
        >
          {icon}
        </div>
        {trend && (
          <div
            className={`stat-card-enhanced-trend ${trend === "up" ? "positive" : "negative"}`}
          >
            {trend === "up" ? "↑" : "↓"} {trendValue}%
          </div>
        )}
      </div>
      <div className="stat-card-enhanced-body">
        <div className="stat-card-enhanced-label">{label}</div>
        <div className="stat-card-enhanced-value">{value}</div>
        {sub && <div className="stat-card-enhanced-sub">{sub}</div>}
      </div>
      <div
        className="stat-card-enhanced-glow"
        style={{
          background: `radial-gradient(circle, ${color}20 0%, transparent 70%)`,
        }}
      />
    </div>
  );
}

// ── Enhanced Badge ─────────────────────────────────────────
export function Badge({ status, variant = "default" }) {
  const badgeClass = status
    ? status.toLowerCase().replace(/\s+/g, "-")
    : "default";
  return (
    <div className={`badge-enhanced ${badgeClass} ${variant}`}>
      <span className="badge-enhanced-dot" />
      {status}
    </div>
  );
}

// ── Enhanced Input ─────────────────────────────────────────
export function Input({
  label,
  icon,
  error,
  helper,
  className = "",
  ...props
}) {
  return (
    <div className="input-group-enhanced">
      {label && <label className="input-label-enhanced">{label}</label>}
      <div className="input-wrapper-enhanced">
        {icon && <span className="input-icon-enhanced">{icon}</span>}
        <input
          {...props}
          className={`form-control-enhanced ${icon ? "with-icon" : ""} ${error ? "error" : ""} ${className}`}
        />
      </div>
      {error && <span className="input-error-enhanced">{error}</span>}
      {helper && !error && (
        <span className="input-helper-enhanced">{helper}</span>
      )}
    </div>
  );
}

// ── Enhanced Select ────────────────────────────────────────
export function Select({ label, icon, children, className = "", ...props }) {
  return (
    <div className="input-group-enhanced">
      {label && <label className="input-label-enhanced">{label}</label>}
      <div className="input-wrapper-enhanced">
        {icon && <span className="input-icon-enhanced">{icon}</span>}
        <select
          {...props}
          className={`form-control-enhanced ${icon ? "with-icon" : ""} ${className}`}
        >
          {children}
        </select>
      </div>
    </div>
  );
}

// ── Enhanced Button ────────────────────────────────────────
export function Btn({
  children,
  variant = "primary",
  size = "md",
  loading = false,
  icon,
  className = "",
  ...props
}) {
  return (
    <button
      {...props}
      className={`btn-enhanced ${variant} ${size} ${loading ? "loading" : ""} ${className}`}
      disabled={loading || props.disabled}
    >
      {loading ? (
        <span className="btn-enhanced-spinner" />
      ) : (
        icon && <span className="btn-enhanced-icon">{icon}</span>
      )}
      {children}
    </button>
  );
}

// ── Enhanced Modal ─────────────────────────────────────────
export function Modal({
  title,
  onClose,
  children,
  size = "md",
  showClose = true,
}) {
  return (
    <div
      className="modal-overlay-enhanced"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className={`modal-content-enhanced ${size}`}>
        {showClose && (
          <button onClick={onClose} className="modal-close-enhanced">
            <FiX />
          </button>
        )}
        <div className="modal-body-enhanced">{children}</div>
      </div>
    </div>
  );
}

// ── Enhanced Table ─────────────────────────────────────────
export function Table({
  columns,
  data,
  loading,
  emptyMsg = "No data found",
  onRowClick,
}) {
  return (
    <div className="table-wrapper-enhanced">
      <table className="data-table-enhanced">
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key} style={{ width: c.width }}>
                {c.label}
                {c.sortable && <span className="table-sort-icon">↕</span>}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            [...Array(5)].map((_, i) => (
              <tr key={i} className="skeleton-row">
                {columns.map((c) => (
                  <td key={c.key}>
                    <div
                      className="skeleton-cell"
                      style={{ width: c.width || "auto" }}
                    />
                  </td>
                ))}
              </tr>
            ))
          ) : data.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="empty-table-msg-enhanced">
                <div className="empty-state-enhanced">
                  <FiPackage size={32} />
                  <p>{emptyMsg}</p>
                </div>
              </td>
            </tr>
          ) : (
            data.map((row, i) => (
              <tr
                key={i}
                onClick={() => onRowClick?.(row)}
                className={onRowClick ? "clickable" : ""}
              >
                {columns.map((c) => (
                  <td key={c.key}>
                    {c.render ? c.render(row[c.key], row) : (row[c.key] ?? "—")}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

// ── Enhanced Pagination ────────────────────────────────────
export function Pagination({ page, pages, onChange, showTotal = true }) {
  if (pages <= 1) return null;

  const getPageNumbers = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];
    let l;

    for (let i = 1; i <= pages; i++) {
      if (i === 1 || i === pages || (i >= page - delta && i <= page + delta)) {
        range.push(i);
      }
    }

    range.forEach((i) => {
      if (l) {
        if (i - l === 2) {
          rangeWithDots.push(l + 1);
        } else if (i - l !== 1) {
          rangeWithDots.push("...");
        }
      }
      rangeWithDots.push(i);
      l = i;
    });
    return rangeWithDots;
  };

  return (
    <div className="pagination-wrapper-enhanced">
      {showTotal && (
        <div className="pagination-info">
          Page {page} of {pages}
        </div>
      )}
      <div className="pagination-controls">
        <Btn
          variant="ghost"
          size="sm"
          disabled={page === 1}
          onClick={() => onChange(page - 1)}
          icon={<FiChevronLeft size={14} />}
        >
          Prev
        </Btn>
        {getPageNumbers().map((p, idx) =>
          p === "..." ? (
            <span key={idx} className="pagination-dots">
              ...
            </span>
          ) : (
            <button
              key={idx}
              onClick={() => onChange(p)}
              className={`pagination-page ${p === page ? "active" : ""}`}
            >
              {p}
            </button>
          ),
        )}
        <Btn
          variant="ghost"
          size="sm"
          disabled={page === pages}
          onClick={() => onChange(page + 1)}
          iconRight={<FiChevronRight size={14} />}
        >
          Next
        </Btn>
      </div>
    </div>
  );
}

// ── Enhanced Bar Chart ─────────────────────────────────────
export function BarChart({
  data,
  labelKey,
  valueKey,
  color = C.primary,
  height = 160,
}) {
  if (!data?.length)
    return (
      <div className="barchart-empty-enhanced">
        <FiBarChart2 size={24} />
        <span>No data available</span>
      </div>
    );

  const max = Math.max(...data.map((d) => d[valueKey]));

  return (
    <div className="barchart-wrapper-enhanced" style={{ height }}>
      {data.map((d, i) => {
        const percentage = (d[valueKey] / max) * 100;
        return (
          <div key={i} className="barchart-col-enhanced">
            <div className="barchart-bar-container">
              <div
                className="barchart-bar-enhanced"
                style={{
                  height: `${Math.max(4, percentage)}%`,
                  background: `linear-gradient(180deg, ${color}, ${color}cc)`,
                }}
              >
                <span className="barchart-value-tooltip">{d[valueKey]}</span>
              </div>
            </div>
            <div className="barchart-label-enhanced">{d[labelKey]}</div>
          </div>
        );
      })}
    </div>
  );
}

// ── Loading Spinner ────────────────────────────────────────
export function LoadingSpinner({ size = "md", text }) {
  return (
    <div className="spinner-wrapper-enhanced">
      <div className={`spinner-enhanced ${size}`} />
      {text && <span className="spinner-text-enhanced">{text}</span>}
    </div>
  );
}

// ── Empty State ────────────────────────────────────────────
export function EmptyState({ icon, title, description, action }) {
  return (
    <div className="empty-state-enhanced">
      <div className="empty-state-icon">{icon || <FiPackage size={48} />}</div>
      <h3 className="empty-state-title">{title || "No data found"}</h3>
      <p className="empty-state-description">
        {description ||
          "Try adjusting your search or filter to find what you're looking for."}
      </p>
      {action && (
        <Btn onClick={action.onClick} icon={action.icon}>
          {action.label}
        </Btn>
      )}
    </div>
  );
}

// ── Search Bar ─────────────────────────────────────────────
export function SearchBar({
  value,
  onChange,
  placeholder = "Search...",
  onFilterClick,
}) {
  return (
    <div className="search-bar-enhanced">
      <div className="search-input-wrapper">
        <FiSearch className="search-icon-enhanced" />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="search-input-enhanced"
        />
        {value && (
          <button
            onClick={() => onChange("")}
            className="search-clear-enhanced"
          >
            <FiX size={14} />
          </button>
        )}
      </div>
      {onFilterClick && (
        <Btn
          variant="ghost"
          size="sm"
          icon={<FiFilter size={14} />}
          onClick={onFilterClick}
        >
          Filter
        </Btn>
      )}
    </div>
  );
}

// ── Role-gated page definitions ────────────────────────────
const PAGES = [
  {
    id: "dashboard",
    label: "Dashboard",
    icon: <MdDashboard />,
    path: "/admin/dashboard",
    minRole: "employee",
    description: "Overview & analytics",
  },
  {
    id: "orders",
    label: "Orders",
    icon: <FiPackage />,
    path: "/admin/orders",
    minRole: "employee",
    description: "Manage customer orders",
  },
  {
    id: "products",
    label: "Products",
    icon: <FiTag />,
    path: "/admin/products",
    minRole: "admin",
    description: "Product catalog",
  },
  {
    id: "category",
    label: "Categories",
    icon: <MdCategory />,
    path: "/admin/category",
    minRole: "admin",
    description: "Organize products",
  },
  {
    id: "coupons",
    label: "Coupons",
    icon: <FiGift />,
    path: "/admin/coupons",
    minRole: "admin",
    description: "Discount management",
  },
  {
    id: "staff",
    label: "Staff",
    icon: <FiUserPlus />,
    path: "/admin/staff",
    minRole: "admin",
    description: "Team management",
  },
  {
    id: "users",
    label: "Users",
    icon: <FiUsers />,
    path: "/admin/users",
    minRole: "superadmin",
    description: "Customer management",
  },
];

function canAccess(userRole, minRole) {
  return (ROLE_HIERARCHY[userRole] ?? 0) >= (ROLE_HIERARCHY[minRole] ?? 99);
}

// ── Sidebar Navigation Item ────────────────────────────────
function SidebarNavItem({ page, isActive, isCollapsed, onClick }) {
  return (
    <button
      className={`sidebar-nav-item-enhanced ${isActive ? "active" : ""}`}
      onClick={onClick}
      title={isCollapsed ? page.label : ""}
    >
      <span className="nav-icon-enhanced">{page.icon}</span>
      {!isCollapsed && (
        <>
          <div className="nav-text-enhanced">
            <span className="nav-label-enhanced">{page.label}</span>
            <span className="nav-desc-enhanced">{page.description}</span>
          </div>
          {isActive && <span className="nav-active-indicator" />}
        </>
      )}
    </button>
  );
}

// ── Main AdminPanel Component ──────────────────────────────
export default function AdminPanel({ user, logout, updateUser }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [toasts, setToasts] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuOpen && !event.target.closest(".user-menu-enhanced")) {
        setUserMenuOpen(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [userMenuOpen]);

  const allowedPages = useMemo(
    () => PAGES.filter((p) => canAccess(user?.role, p.minRole)),
    [user?.role],
  );

  const getCurrentPage = () => {
    const page = PAGES.find((p) => location.pathname === p.path);
    return page ? page.id : (allowedPages[0]?.id ?? "dashboard");
  };

  const [activePage, setActivePage] = useState(getCurrentPage);

  useEffect(() => {
    setActivePage(getCurrentPage());
  }, [location.pathname]);

  const toast = useCallback((msg, type = "success") => {
    const id = Date.now();
    setToasts((p) => [...p, { id, msg, type }]);
    setTimeout(() => setToasts((p) => p.filter((t) => t.id !== id)), 4000);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((p) => p.filter((t) => t.id !== id));
  }, []);

  const handleNavigation = (path, pageId) => {
    setActivePage(pageId);
    navigate(path);
    setMobileMenuOpen(false);
  };

  const handleLogout = () => {
    logout();
  };

  const activeMeta = PAGES.find((p) => p.id === activePage);
  const roleMeta = ROLE_META[user?.role] ?? ROLE_META.user;
  const defaultPath = allowedPages[0]?.path ?? "/login";

  const toggleSidebar = () => {
    if (window.innerWidth < 1024) {
      setMobileMenuOpen(!mobileMenuOpen);
    } else {
      setSidebarCollapsed(!sidebarCollapsed);
    }
  };

  return (
    <>
      <div className="admin-layout-enhanced">
        {/* Sidebar Overlay for Mobile */}
        {mobileMenuOpen && (
          <div
            className="sidebar-overlay-enhanced"
            onClick={() => setMobileMenuOpen(false)}
          />
        )}

        {/* Sidebar */}
        <aside
          className={`sidebar-enhanced ${sidebarCollapsed ? "collapsed" : ""} ${mobileMenuOpen ? "mobile-open" : ""}`}
        >
          <div className="sidebar-header-enhanced">
            <div className="sidebar-logo-enhanced">
              <div className="logo-icon-enhanced">
                <FaCrown />
              </div>
              {!sidebarCollapsed && (
                <div className="logo-text-enhanced">
                  <span className="logo-name">KKB & Co.</span>
                  <span className="logo-badge">Admin</span>
                </div>
              )}
            </div>
            <button onClick={toggleSidebar} className="sidebar-toggle-enhanced">
              {sidebarCollapsed ? <FiChevronRight /> : <FiChevronLeft />}
            </button>
          </div>

          {/* User Profile Badge */}
          {!sidebarCollapsed && (
            <div className="user-profile-badge-enhanced">
              <div
                className="user-avatar-enhanced"
                style={{ background: roleMeta.gradient }}
              >
                {user?.name?.charAt(0)?.toUpperCase()}
              </div>
              <div className="user-info-enhanced">
                <div className="user-name-enhanced">
                  {user?.name?.split(" ")[0]}
                </div>
                <div
                  className="user-role-enhanced"
                  style={{ color: roleMeta.color }}
                >
                  {roleMeta.icon} {roleMeta.label}
                </div>
              </div>
            </div>
          )}

          <div className="sidebar-nav-enhanced">
            {allowedPages.map((p) => (
              <SidebarNavItem
                key={p.id}
                page={p}
                isActive={activePage === p.id}
                isCollapsed={sidebarCollapsed}
                onClick={() => handleNavigation(p.path, p.id)}
              />
            ))}
          </div>

          <div className="sidebar-footer-enhanced">
            <button onClick={handleLogout} className="sidebar-logout-enhanced">
              <FiLogOut />
              {!sidebarCollapsed && <span>Sign Out</span>}
            </button>
          </div>
        </aside>

        {/* Main Content */}
        <main className="main-container-enhanced">
          <header className="topbar-enhanced">
            <div className="topbar-left-enhanced">
              <button onClick={toggleSidebar} className="menu-toggle-enhanced">
                <FiMenu />
              </button>
              <div className="breadcrumb-enhanced">
                <span className="breadcrumb-item">Admin</span>
                <FiChevronRight className="breadcrumb-sep" />
                <span className="breadcrumb-current">{activeMeta?.label}</span>
              </div>
            </div>
            <div className="topbar-right-enhanced">
              <div
                className="user-menu-enhanced"
                onClick={() => setUserMenuOpen(!userMenuOpen)}
              >
                <div
                  className="user-avatar-small"
                  style={{ background: roleMeta.gradient }}
                >
                  {user?.name?.charAt(0)?.toUpperCase()}
                </div>

                {/* User Dropdown Menu */}
                {userMenuOpen && (
                  <div className="user-dropdown-enhanced">
                    <div className="user-dropdown-header">
                      <div
                        className="user-dropdown-avatar"
                        style={{ background: roleMeta.gradient }}
                      >
                        {user?.name?.charAt(0)?.toUpperCase()}
                      </div>
                      <div className="user-dropdown-info">
                        <div className="user-dropdown-name">{user?.name}</div>
                        <div
                          className="user-dropdown-role"
                          style={{ color: roleMeta.color }}
                        >
                          {roleMeta.icon} {roleMeta.label}
                        </div>
                      </div>
                    </div>

                    <div className="user-dropdown-divider" />

                    <div className="user-dropdown-details">
                      <div className="user-detail-item">
                        <span className="user-detail-label">Email</span>
                        <span className="user-detail-value">
                          {user?.email || "user@example.com"}
                        </span>
                      </div>
                      <div className="user-detail-item">
                        <span className="user-detail-label">Role</span>
                        <span className="user-detail-value">
                          {roleMeta.label}
                        </span>
                      </div>
                      <div className="user-detail-item">
                        <span className="user-detail-label">Member Since</span>
                        <span className="user-detail-value">
                          {user?.joinDate || "Jan 2024"}
                        </span>
                      </div>
                    </div>

                    <div className="user-dropdown-divider" />

                    <button
                      onClick={handleLogout}
                      className="user-dropdown-logout"
                    >
                      <FiLogOut />
                      <span>Sign Out</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </header>

          <div className="page-container-enhanced">
            <div className="page-header-enhanced">
              <div className="page-title-section">
                <div
                  className="page-icon-enhanced"
                  style={{ background: roleMeta.gradient }}
                >
                  {activeMeta?.icon}
                </div>
                <div>
                  <h1 className="page-title-enhanced">{activeMeta?.label}</h1>
                  <p className="page-description-enhanced">
                    {activeMeta?.description}
                  </p>
                </div>
              </div>
              {/* <div className="page-actions-enhanced">
                <Btn variant="ghost" size="sm" icon={<FiDownload size={14} />}>
                  Export
                </Btn>
              </div> */}
            </div>

            <div className="page-content-enhanced">
              <Routes>
                {canAccess(user?.role, "employee") && (
                  <>
                    <Route
                      path="/dashboard"
                      element={<Dashboard toast={toast} />}
                    />
                    <Route path="/orders" element={<Orders toast={toast} />} />
                    <Route
                      path="/contacts"
                      element={<Contacts toast={toast} />}
                    />
                  </>
                )}
                {canAccess(user?.role, "admin") && (
                  <>
                    <Route
                      path="/products"
                      element={<Products toast={toast} />}
                    />
                    <Route
                      path="/category"
                      element={<Categories toast={toast} />}
                    />
                    <Route
                      path="/coupons"
                      element={<Coupons toast={toast} />}
                    />
                    <Route
                      path="/staff"
                      element={<Staff toast={toast} user={user} />}
                    />
                  </>
                )}
                {canAccess(user?.role, "superadmin") && (
                  <Route path="/users" element={<Users toast={toast} />} />
                )}
                <Route
                  path="*"
                  element={<Navigate to={defaultPath} replace />}
                />
              </Routes>
            </div>
          </div>
        </main>
      </div>

      <Toast toasts={toasts} removeToast={removeToast} />
    </>
  );
}
